/**
 * Types du domaine de la console Siraj.
 * Contrat partagé avec backend-console (voir openapi-notes.md).
 * Les dates sont des chaînes ISO 8601 (UTC) ; les identifiants sont opaques.
 */

export type ISODate = string;
export type OrgId = string;

/* ---------- Pagination et requêtes ---------- */

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PageParams {
  page?: number;
  pageSize?: number;
  /** Recherche plein texte. */
  q?: string;
}

/* ---------- Permissions et rôles ---------- */

export const PERMISSIONS = [
  'devices.read',
  'devices.manage',
  'devices.revoke',
  'groups.manage',
  'members.read',
  'members.invite',
  'members.manage',
  'roles.manage',
  'policies.read',
  'policies.manage',
  'audit.read',
  'audit.export',
  'sessions.read',
  'sessions.recordings',
  /** Zone « Satisfaction » : notes et commentaires des clients par technicien. */
  'ratings.read',
  'deployment.manage',
  'billing.read',
  'billing.manage',
  'settings.read',
  'settings.manage',
  'integrations.manage',
  'apikeys.manage',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export type SystemRoleId = 'owner' | 'admin' | 'supervisor' | 'technician' | 'billing' | 'viewer';

export interface Role {
  id: string;
  name: string;
  description: string;
  /** Rôle prédéfini non supprimable. */
  system: boolean;
  permissions: Permission[];
  memberCount: number;
}

/* ---------- Identité et organisations ---------- */

export type PlanId = 'free' | 'pro' | 'team' | 'enterprise';

export interface Organization {
  id: OrgId;
  name: string;
  slug: string;
  plan: PlanId;
  createdAt: ISODate;
  /** Région de stockage des données. */
  region: 'eu' | 'mena';
  domain?: string;
  require2fa: boolean;
}

export interface OrganizationSettings extends Organization {
  whiteLabel: { enabled: boolean; logoUrl?: string; primaryColor: string; accentColor?: string; customDomain?: string; domainVerified?: boolean };
  /** Fuseau IANA (ex. `Africa/Algiers`) et langue par défaut des nouveaux membres. */
  timezone?: string;
  defaultLocale?: 'fr' | 'en' | 'ar';
  /** Rétention des enregistrements de sessions. */
  recordingRetentionDays?: number;
  security: {
    sessionTimeoutMin: number;
    allowedCountries: string[];
    blockedIps: string[];
    trustedDevicesOnly: boolean;
  };
  auditRetentionDays: number;
}

export interface Membership {
  orgId: OrgId;
  orgName: string;
  roleId: string;
  roleName: string;
  /** Permissions déjà résolues par le serveur pour ce rôle. */
  permissions: Permission[];
}

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  memberships: Membership[];
  twoFactorEnabled: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export type LoginResult =
  | { status: 'ok'; token: string; user: CurrentUser }
  | { status: 'totp_required'; challengeId: string };

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  /** Nom de la nouvelle organisation ; son créateur en devient propriétaire. */
  orgName: string;
}

export interface VerifyTotpRequest {
  challengeId: string;
  code: string;
}

export interface AuthSuccess {
  token: string;
  user: CurrentUser;
}

/* ---------- Parc ---------- */

export type OsFamily = 'windows' | 'macos' | 'linux' | 'android' | 'ios';
export type DeviceStatus = 'online' | 'offline';

export interface DeviceInventory {
  cpu: string;
  ramGb: number;
  diskGb: number;
  ramUsedPct: number;
  diskUsedPct: number;
  /** Antivirus principal ('' si inconnu) et état de sa protection. */
  antivirusName: string;
  antivirusStatus: 'protected' | 'unprotected' | 'unknown';
  /** Logiciels installés (renvoyé par le détail d'un appareil uniquement). */
  software?: { name: string; version: string }[];
}

/* ---------- Parc : alertes et identité (zones fleet / people) ---------- */

/** Seuils d'alerte de l'organisation (disque, mémoire, hors ligne) et alertes de crédits IA. */
export interface AlertSettings {
  diskPct: number;
  ramPct: number;
  offlineDays: number;
  /** Alertes à 80 % et 100 % de l'allocation mensuelle de crédits IA (webhook `ai_credits.alert`, audit) ; actif par défaut. */
  aiCredits: boolean;
}

export type SsoProtocol = 'oidc' | 'saml';
export type SsoProvider = 'entra' | 'google' | 'custom';

/** Configuration SSO d'une organisation (secrets jamais renvoyés). */
export interface SsoConfig {
  protocol: SsoProtocol;
  provider: SsoProvider;
  /** Émetteur OIDC ou URL de métadonnées SAML. */
  issuer: string;
  clientId: string;
  /** Domaine de messagerie rattaché (ex. tassili.dz). */
  domain: string;
  /** Impose le SSO : plus de mot de passe local. */
  enforce: boolean;
  /** Dernier test réussi. */
  lastTestedAt: ISODate | null;
}

export interface Device {
  id: string;
  /** Identifiant Siraj à 9 chiffres affiché à l'utilisateur. */
  sirajId: string;
  name: string;
  hostname: string;
  os: OsFamily;
  osVersion: string;
  status: DeviceStatus;
  lastSeenAt: ISODate;
  enrolledAt: ISODate;
  groupId: string | null;
  tags: string[];
  agentVersion: string;
  channel: ReleaseChannelId;
  ipAddress: string;
  inventory: DeviceInventory;
  openAlerts: number;
}

export interface DeviceListParams extends PageParams {
  status?: DeviceStatus;
  groupId?: string;
  os?: OsFamily;
  /** Tri côté serveur. */
  sort?: 'name' | 'lastSeenAt' | 'os' | 'status';
  direction?: 'asc' | 'desc';
}

export interface DeviceGroup {
  id: string;
  name: string;
  description: string;
  parentId: string | null;
  deviceCount: number;
}

export interface PresenceEvent {
  deviceId: string;
  status: DeviceStatus;
  at: ISODate;
}

/* ---------- Personnes ---------- */

export type MemberStatus = 'active' | 'suspended' | 'invited';

export interface Member {
  id: string;
  email: string;
  name: string;
  roleId: string;
  groupIds: string[];
  status: MemberStatus;
  twoFactorEnabled: boolean;
  lastSeenAt: ISODate | null;
  createdAt: ISODate;
}

export interface MemberListParams extends PageParams {
  roleId?: string;
  status?: MemberStatus;
}

export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

export interface Invitation {
  id: string;
  email: string;
  roleId: string;
  groupIds: string[];
  invitedBy: string;
  /** Nom (à défaut e-mail) de l'invitant, résolu par le serveur ; `null` si inconnu (afficher alors `invitedBy`). */
  invitedByName?: string | null;
  invitedByEmail?: string | null;
  createdAt: ISODate;
  expiresAt: ISODate;
  status: InvitationStatus;
}

export interface CreateInvitationRequest {
  email: string;
  roleId: string;
  groupIds: string[];
}

/* ---------- Politiques ---------- */

export type ClipboardMode = 'both' | 'to_host' | 'from_host' | 'none';

export interface AccessWindow {
  /** 0 = dimanche … 6 = samedi. */
  days: number[];
  start: string; // HH:MM
  end: string; // HH:MM
}

export interface PolicyRules {
  clipboard: ClipboardMode;
  blockedFileTypes: string[];
  maxTransferMb: number | null;
  privacyScreen: boolean;
  recordingRequired: boolean;
  accessWindows: AccessWindow[];
  requireApproval: boolean;
  approverRoleId: string | null;
}

export interface Policy {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  /** Mode test : journalise sans appliquer. */
  dryRun: boolean;
  rules: PolicyRules;
  groupIds: string[];
  roleIds: string[];
  /** Zone politiques : affectation à des appareils / techniciens précis (absent = aucun). */
  deviceIds?: string[];
  memberIds?: string[];
  version: number;
  updatedAt: ISODate;
  updatedBy: string;
  /** Nom (à défaut e-mail) de l'auteur de la dernière version, résolu par le serveur ; `null` si inconnu. */
  updatedByName?: string | null;
  updatedByEmail?: string | null;
}

export type PolicyInput = Omit<Policy, 'id' | 'version' | 'updatedAt' | 'updatedBy'>;

export interface PolicyPreview {
  affectedDevices: number;
  affectedMembers: number;
  /** Phrases/indicateurs lisibles décrivant l'effet. */
  warnings: string[];
  /** Politiques déjà actives qui ciblent les mêmes appareils (la plus restrictive l'emporte). */
  conflicts?: { policyId: string; policyName: string; devices: number }[];
}

/* -- Zone politiques : entrée d'aperçu, versions, diffusion -- */

export type PolicyPreviewInput = Pick<PolicyInput, 'groupIds' | 'roleIds' | 'rules'> & {
  deviceIds?: string[];
  memberIds?: string[];
  /** Politique en cours d'édition (exclue de la détection de conflits). */
  policyId?: string;
};

/** Instantané immuable d'une version de politique. */
export interface PolicyVersion {
  version: number;
  at: ISODate;
  by: string;
  /** Nom (à défaut e-mail) de l'auteur, résolu par le serveur ; `null` si inconnu. */
  byName?: string | null;
  byEmail?: string | null;
  /** Version restaurée si cette version est un retour arrière. */
  rollbackOf?: number;
  enabled: boolean;
  dryRun: boolean;
  rules: PolicyRules;
  groupIds: string[];
  roleIds: string[];
  deviceIds: string[];
  memberIds: string[];
}

export type DeliveryStatus = 'acknowledged' | 'pending' | 'offline' | 'failed';

export interface PolicyDeliveryItem {
  deviceId: string;
  deviceName: string;
  status: DeliveryStatus;
  ackedAt?: ISODate;
}

/** Progression de diffusion de la version courante : accusé de réception par agent. */
export interface PolicyDelivery {
  policyId: string;
  version: number;
  total: number;
  acknowledged: number;
  pending: number;
  offline: number;
  failed: number;
  items: PolicyDeliveryItem[];
}

/* ---------- Audit et sessions ---------- */

export type AuditOutcome = 'success' | 'failure' | 'denied';
export type AuditActorType = 'member' | 'device' | 'system' | 'api';

export interface AuditEvent {
  id: string;
  at: ISODate;
  actor: { type: AuditActorType; id: string; name: string; email?: string };
  /** Verbe pointé : `session.start`, `member.invite`, `policy.update`… */
  action: string;
  target?: { type: string; id: string; name: string };
  outcome: AuditOutcome;
  ip?: string;
  country?: string;
  /** Signature d'intégrité (chaînage de hash) vérifiée côté serveur. */
  signatureValid: boolean;
  metadata?: Record<string, string | number | boolean>;
}

export interface AuditListParams extends PageParams {
  action?: string;
  actorId?: string;
  outcome?: AuditOutcome;
  from?: ISODate;
  to?: ISODate;
  /** Zone audit : événements visant cet appareil, filtre d'intégrité et tri serveur. */
  deviceId?: string;
  signature?: 'valid' | 'invalid';
  sort?: 'at' | 'actor' | 'action' | 'outcome';
  direction?: 'asc' | 'desc';
}

export type ExportFormat = 'csv' | 'json';

export type SessionMode = 'attended' | 'unattended';
export type SessionStatus = 'active' | 'ended' | 'failed';

export interface RemoteSession {
  id: string;
  startedAt: ISODate;
  endedAt: ISODate | null;
  durationSec: number;
  technicianId: string;
  technicianName: string;
  deviceId: string;
  deviceName: string;
  mode: SessionMode;
  status: SessionStatus;
  recorded: boolean;
  actions: number;
  bytesTransferred: number;
  aiSummary?: string;
  clientRating?: number;
  /** Commentaire facultatif laissé par le client avec sa note. */
  clientComment?: string;
  ticketRef?: string;
  /** Détail (getSession) : participants et enregistrement. */
  participants?: SessionParticipant[];
  recording?: SessionRecording;
}

export interface SessionParticipant {
  name: string;
  role: 'technician' | 'client' | 'observer';
  joinedAt: ISODate;
  leftAt: ISODate | null;
}

export type RecordingConsent = 'granted' | 'pending' | 'denied';

export interface SessionRecording {
  sizeMb: number;
  durationSec: number;
  encrypted: boolean;
  /** Consentement du client à la relecture. */
  consent: RecordingConsent;
  /** Fin de rétention : l'enregistrement est ensuite purgé. */
  retentionUntil: ISODate;
  purged: boolean;
}

/** Autorisation de lecture d'un enregistrement (courte durée, journalisée). */
export interface RecordingAccess {
  sessionId: string;
  grantedAt: ISODate;
  expiresAt: ISODate;
  durationSec: number;
}

export interface SessionListParams extends PageParams {
  status?: SessionStatus;
  technicianId?: string;
  deviceId?: string;
  from?: ISODate;
  to?: ISODate;
}

/* ---------- Satisfaction (notes des clients) ---------- */

/** Moyenne et volume de notes sur une période ; `average` est null sans note. */
export interface RatingStats {
  average: number | null;
  count: number;
}

/** Note de 1 à 5 laissée par le client en fin de session. */
export interface SessionRating {
  id: string;
  technicianId: string;
  technicianName: string | null;
  rating: number;
  comment: string | null;
  sessionId: string | null;
  createdAt: ISODate;
}

export interface TechnicianRatingSummary {
  technicianId: string;
  name: string | null;
  email: string | null;
  /** Moyenne sur la fenêtre (au moins une note : jamais nulle). */
  average: number;
  ratingCount: number;
  currentMonth: RatingStats;
  previousMonth: RatingStats;
}

/** Synthèse de l'équipe et par technicien (12 derniers mois). */
export interface RatingSummary {
  /** Fenêtre couverte, mois `YYYY-MM` inclus. */
  period: { from: string; to: string };
  team: RatingStats & { currentMonth: RatingStats; previousMonth: RatingStats };
  /** Techniciens ayant au moins une note dans la fenêtre, triés par moyenne décroissante. */
  technicians: TechnicianRatingSummary[];
}

/** Mois calendaire `YYYY-MM`. */
export interface MonthlyRating extends RatingStats {
  month: string;
}

/** Évolution mensuelle d'un technicien, du plus ancien au plus récent, mois vides inclus (zéros sans note). */
export interface TechnicianRatingHistory extends RatingStats {
  technicianId: string;
  name: string | null;
  email: string | null;
  months: MonthlyRating[];
}

/** Liste des notes : pagination par curseur opaque (et non page/pageSize). */
export interface RatingListParams {
  technicianId?: string;
  /** Bornes ISO incluses. */
  from?: ISODate;
  to?: ISODate;
  cursor?: string;
  /** 1 à 200, 50 par défaut. */
  limit?: number;
}

export interface RatingPage {
  items: SessionRating[];
  total: number;
  /** Curseur de la page suivante ; null en fin de liste. */
  nextCursor: string | null;
}

/* ---------- Déploiement ---------- */

export type ReleaseChannelId = 'stable' | 'beta';

export interface Release {
  version: string;
  channel: ReleaseChannelId;
  publishedAt: ISODate;
  adoptionPct: number;
  notes: string;
}

export type EnrollmentTokenStatus = 'active' | 'revoked' | 'expired' | 'exhausted';

export interface EnrollmentToken {
  id: string;
  label: string;
  /** Début du jeton seulement ; le secret n'est montré qu'à la création. */
  tokenPreview: string;
  groupId: string | null;
  channel: ReleaseChannelId;
  createdAt: ISODate;
  expiresAt: ISODate;
  maxUses: number | null;
  uses: number;
  status: EnrollmentTokenStatus;
  createdBy: string;
}

export interface CreateEnrollmentTokenRequest {
  label: string;
  groupId: string | null;
  channel: ReleaseChannelId;
  validDays: number;
  maxUses: number | null;
}

export interface CreatedEnrollmentToken extends EnrollmentToken {
  /** Secret en clair, renvoyé une seule fois. */
  secret: string;
}

/* ---------- Facturation ---------- */

export type BillingMode = 'per_device' | 'per_technician';
export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'suspended' | 'canceled';

export interface UsageMeter {
  used: number;
  limit: number | null;
}

/** Crédits IA de la période courante : allocation mensuelle sans report, puis solde acheté (sans expiration). */
export interface AiCreditUsage {
  periodStart: ISODate;
  periodEnd: ISODate;
  allowance: number;
  allowanceUsed: number;
  purchasedBalance: number;
  reserved: number;
  remaining: number;
}

export interface Subscription {
  planId: PlanId;
  billingMode: BillingMode;
  status: SubscriptionStatus;
  technicians: UsageMeter;
  /** Nombre de techniciens souscrit (Pro, Équipe), base du tarif par technicien ; absent sinon. */
  technicianSeats?: number;
  devices: UsageMeter;
  /** Jauge mensuelle : consommé de l'allocation de la période / allocation de la période. */
  aiCredits: UsageMeter;
  /** Détail des crédits IA de la période (même forme que `AiCreditUsage` côté serveur) ; absent sur un serveur plus ancien. */
  aiCreditsUsage?: AiCreditUsage;
  currentPeriodStart?: ISODate;
  currentPeriodEnd: ISODate;
  /** Fin de l'essai PRO ; présent aussi une fois l'essai terminé (statut ≠ `trialing`) : l'essai est alors consommé. */
  trialEndsAt?: ISODate;
  /** Impayé : date limite avant suspension du service. */
  graceEndsAt?: ISODate;
  paymentMethod?: { brand: 'cib' | 'visa' | 'mastercard'; last4: string; expiry: string };
}

/** Plan du catalogue serveur (`GET /v1/plans`) : quotas et fonctionnalités paramétrables. */
export interface PlanDefinition {
  id: string;
  name: string;
  description: string;
  /** Prix mensuel en centimes ; `null` = sur devis. */
  priceMonthlyCents: number | null;
  quotas: { technicians: number; devices: number };
  features: string[];
  sortOrder: number;
  active: boolean;
}

export type InvoiceStatus = 'paid' | 'open' | 'failed' | 'refunded';

export interface Invoice {
  id: string;
  number: string;
  issuedAt: ISODate;
  amount: number;
  currency: string;
  status: InvoiceStatus;
  provider: 'stripe' | 'satim';
}

/* --- Moyens de paiement (jetons uniquement : jamais de numéro de carte côté console ni côté serveur Siraj) --- */

export type PaymentProvider = 'stripe' | 'satim';

export interface PaymentMethod {
  id: string;
  provider: PaymentProvider;
  brand: 'cib' | 'visa' | 'mastercard';
  last4: string;
  expiry: string;
  isDefault: boolean;
  createdAt: ISODate;
}

export interface AddPaymentMethodRequest {
  provider: PaymentProvider;
  /** Jeton opaque renvoyé par le champ hébergé du prestataire (SATIM/Stripe). */
  token: string;
  brand: PaymentMethod['brand'];
  last4: string;
  expiry: string;
}

export interface PurchaseCreditsRequest {
  /** Nombre de crédits IA achetés (le prix est calculé côté serveur). */
  credits: number;
}

/* ---------- Intégrations et clés d'API ---------- */

export type IntegrationKind = 'freshdesk' | 'zendesk' | 'glpi' | 'jira_sm' | 'sso_oidc' | 'sso_saml' | 'scim';

export interface Integration {
  id: string;
  kind: IntegrationKind;
  name: string;
  status: 'connected' | 'disconnected' | 'error';
  connectedAt?: ISODate;
}

export interface ApiKey {
  id: string;
  name: string;
  /** Préfixe visible (`sk_live_ab12…`). */
  prefix: string;
  scopes: Permission[];
  createdAt: ISODate;
  createdBy: string;
  lastUsedAt: ISODate | null;
  expiresAt: ISODate | null;
}

export interface CreateApiKeyRequest {
  name: string;
  scopes: Permission[];
  expiresInDays: number | null;
}

export interface CreatedApiKey extends ApiKey {
  secret: string;
}

/* ---------- Tableau de bord ---------- */

export type AlertSeverity = 'info' | 'warning' | 'critical';
export type AlertKind = 'offline' | 'disk' | 'ram' | 'agent_outdated' | 'policy_denied';

export interface DeviceAlert {
  id: string;
  severity: AlertSeverity;
  kind: AlertKind;
  deviceId: string;
  deviceName: string;
  raisedAt: ISODate;
  /** Valeur mesurée (pourcentage…) quand pertinent. */
  value?: number;
}

export type ServiceComponentStatus = 'operational' | 'degraded' | 'outage';

export interface ServiceStatus {
  updatedAt: ISODate;
  components: { id: string; status: ServiceComponentStatus; latencyMs?: number }[];
}

export interface ActivityPoint {
  /** Jour au format YYYY-MM-DD. */
  date: string;
  sessions: number;
  uniqueDevices: number;
}

export interface DashboardSummary {
  devicesTotal: number;
  devicesOnline: number;
  sessionsToday: number;
  sessionsYesterday: number;
  activeSessions: number;
  alerts: DeviceAlert[];
  usage: { technicians: UsageMeter; aiCredits: UsageMeter; devices: UsageMeter };
  activity7d: ActivityPoint[];
  service: ServiceStatus;
}

/* Zone Paramètres : types étendus (voir settings.types.ts) */
export * from './settings.types';
